import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { DeviceAdminDetail } from 'src/api/Api';
import { DeviceTypeResponse } from 'src/api2/deviceCustodyApi';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

// AI-GENERATED-START
const CONDITION_COLOR: Record<string, string> = {
  NORMAL: '#00E676',
  DAMAGED: '#FF5252',
  MAINTENANCE: '#FFD600',
  MISSING: '#FF5252',
  RETIRED: '#666666',
};

const getConditionColor = (cond: string): string => {
  return CONDITION_COLOR[cond?.toUpperCase()] || '#AAAAAA';
};

interface EditDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  device: DeviceAdminDetail | null;
  onSuccess: (device: DeviceAdminDetail) => void;
  deviceTypes: DeviceTypeResponse[];
  deviceConditions: string[];
}

const EditDevicePage: FC<EditDeviceDialogProps> = ({
  open,
  onClose,
  device,
  onSuccess,
  deviceTypes,
  deviceConditions,
}) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    condition: string;
    device_type_id: string;
  }>({
    name: '',
    condition: '',
    device_type_id: '',
  });

  useEffect(() => {
    if (open && device) {
      setFormData({
        name: device.name || '',
        condition: device.condition || 'NORMAL',
        device_type_id: (device as any)?.device_type?.device_type_id || '',
      });
    }
  }, [open, device]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!device) return;
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        condition: formData.condition,
        device_type_id: formData.device_type_id,
      };
      const response = await clientService.api.updateApiDeviceApiV1AdminDevicesDeviceIdPut(device.device_id, payload as any);
      onSuccess(response.data as DeviceAdminDetail);
      showSnackbar({ message: 'Device updated successfully!', type: SnackbarType.SUCCESS });
      onClose();
    } catch (error: any) {
      console.error('Failed to update device:', error);
      const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to update device';
      showSnackbar({ message: errorMessage, type: SnackbarType.ERROR });
    } finally {
      setLoading(false);
    }
  };

  if (!device) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: '#111',
          color: '#FFF',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          border: '1px solid #222',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Edit Device</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Update the settings and status for this device.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#666' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {/* Device Info Summary */}
        <Box sx={{ bgcolor: '#080808', border: '1px solid #1A1A1A', borderRadius: '12px', p: 2, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#444', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Device ID</Typography>
              <Typography variant="body2" sx={{ color: '#AAA', fontWeight: 600 }}>{device.human_readable_id}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#444', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Serial Number</Typography>
              <Typography variant="body2" sx={{ color: '#AAA', fontWeight: 600 }}>{device.serial_number}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#444', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Type</Typography>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              <Typography variant="body2" sx={{ color: '#AAA', fontWeight: 600 }}>{(device as any)?.device_type?.category ?? (device as any)?.device_type?.name ?? device.type}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#444', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Purchased At</Typography>
              <Typography variant="body2" sx={{ color: '#AAA', fontWeight: 600 }}>{formatDate(device.purchased_at)}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ borderColor: '#1A1A1A', mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>Display Name</Typography>
          <TextField
            fullWidth
            name="name"
            placeholder="Enter device name"
            value={formData.name || ''}
            onChange={handleChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#00E676' },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>Device Type</Typography>
          <TextField
            select
            fullWidth
            name="device_type_id"
            value={formData.device_type_id}
            onChange={handleChange}
            SelectProps={{
              IconComponent: KeyboardArrowDownIcon,
              MenuProps: { PaperProps: { sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' } } }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#00E676' },
              },
            }}
          >
            {deviceTypes.map((type) => (
              <MenuItem key={type.device_type_id} value={type.device_type_id}>{type.name}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>Condition</Typography>
          <TextField
            select
            fullWidth
            name="condition"
            value={formData.condition || ''}
            onChange={handleChange}
            SelectProps={{
              IconComponent: KeyboardArrowDownIcon,
              MenuProps: { PaperProps: { sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' } } }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#00E676' },
              },
            }}
          >
            {deviceConditions.map((cond) => {
              const color = getConditionColor(cond);
              return (
                <MenuItem key={cond} value={cond}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                    {cond}
                  </Box>
                </MenuItem>
              );
            })}
          </TextField>
        </Box>
      </DialogContent>

      <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid #222' }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#AAA', textTransform: 'none', fontWeight: 600,
            '&:hover': { color: '#FFF', bgcolor: 'transparent' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{
            bgcolor: '#00E676', color: '#000', px: 4, borderRadius: '8px',
            textTransform: 'none', fontWeight: 700,
            '&:hover': { bgcolor: '#00c853' }
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default EditDevicePage;

